import React from "react";
import { useClinicalAlerts, useDeleteClinicalAlert } from "../../application/useClinicalAlerts";
import { ClinicalAlert, Criticality } from "../../domain/ClinicalAlert";
import { useTranslation } from "react-i18next";
import { Pagination } from "../../../../shared/ui/common/Pagination";
import { Loader2, Trash2, Edit2, AlertTriangle, Search } from "lucide-react";
import { useAiInsightsGate, LockedState } from "../../../ai_insights";

interface ClinicalAlertListProps {
    onEdit: (alert: ClinicalAlert) => void;
}

const ClinicalAlertList: React.FC<ClinicalAlertListProps> = ({ onEdit }) => {
    const { t } = useTranslation();
    const { data: alerts, isLoading, error } = useClinicalAlerts();
    const { adopted: aiInsightsAdopted, loading: adoptionLoading } = useAiInsightsGate();
    const deleteMutation = useDeleteClinicalAlert();
    const [searchTerm, setSearchTerm] = React.useState("");
    const [criticalityFilter, setCriticalityFilter] = React.useState<Criticality | "all">("all");
    const [offset, setOffset] = React.useState(0);
    const pageSize = 10;

    const filteredAlerts = React.useMemo(() => {
        if (!alerts) return [];
        return alerts.filter(alert => {
            const matchesSearch = alert.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                alert.context.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCriticality = criticalityFilter === "all" || alert.criticality === criticalityFilter;
            return matchesSearch && matchesCriticality;
        });
    }, [alerts, searchTerm, criticalityFilter]);

    const paginatedAlerts = React.useMemo(() => {
        return filteredAlerts.slice(offset, offset + pageSize);
    }, [filteredAlerts, offset, pageSize]);

    const handleNext = () => setOffset(prev => Math.min(prev + pageSize, Math.max(0, filteredAlerts.length - 1)));
    const handlePrev = () => setOffset(prev => Math.max(prev - pageSize, 0));

    React.useEffect(() => {
        setOffset(0);
    }, [searchTerm, criticalityFilter]);

    const handleDelete = async (id: string) => {
        if (window.confirm(t("clinicalAlerts.list.confirmDelete"))) {
            await deleteMutation.mutateAsync(id);
        }
    };

    const getCriticalityBadge = (criticality: Criticality) => {
        const colors = {
            [Criticality.LOW]: "bg-accent-tint text-accent-text border-accent/20",
            [Criticality.MEDIUM]: "bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400 border-orange-100 dark:border-orange-500/20",
            [Criticality.CRITICAL]: "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400 border-red-100 dark:border-red-500/20",
        };
        const label = t(`clinicalAlerts.modal.criticality.${criticality}`);
        return (
            <span className={`px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wider border ${colors[criticality]}`}>
                {label}
            </span>
        );
    };

    if (adoptionLoading || isLoading) {
        return (
            <div className="flex justify-center p-12">
                <Loader2 className="h-10 w-10 animate-spin text-brand-600" />
            </div>
        );
    }

    // Clinical alerts are part of the AI Insights tool: the API returns an empty
    // list when it is not adopted, so render the locked state instead.
    if (!aiInsightsAdopted) {
        return <LockedState title={t("aiInsights.locked.clinicalAlerts")} />;
    }

    if (error) {
        return (
            <div className="p-10 text-center bg-red-50 dark:bg-red-500/5 rounded-2xl border border-red-100 dark:border-red-500/10">
                <p className="text-red-600 dark:text-red-400">{t("clinicalAlerts.list.error")}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 items-center">
                <div className="relative flex-1 group w-full">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-content-tertiary group-focus-within:text-brand-500 transition-colors" />
                    <input
                        type="text"
                        placeholder={t("clinicalAlerts.modal.list.searchPlaceholder", "Search alerts...")}
                        value={searchTerm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                        className="w-full h-12 rounded-2xl border border-gray-100 bg-white/50 px-4 py-2 pl-12 text-sm text-gray-900 focus:border-accent dark:bg-white/[0.03] dark:border-white/5 dark:text-white transition-all shadow-theme-xs group-hover:border-gray-200 dark:group-hover:border-white/10"
                    />
                </div>
                <div className="w-full sm:w-56 group">
                    <select
                        value={criticalityFilter}
                        onChange={(e) => setCriticalityFilter(e.target.value as Criticality | "all")}
                        className="w-full h-12 rounded-2xl border border-gray-100 bg-white/50 px-4 py-2 text-sm text-gray-900 focus:border-accent dark:bg-white/[0.03] dark:border-white/5 dark:text-white transition-all shadow-theme-xs group-hover:border-gray-200 dark:group-hover:border-white/10"
                    >
                        <option value="all">{t("common.allStates", "All levels")}</option>
                        <option value={Criticality.LOW}>{t("clinicalAlerts.modal.criticality.low")}</option>
                        <option value={Criticality.MEDIUM}>{t("clinicalAlerts.modal.criticality.medium")}</option>
                        <option value={Criticality.CRITICAL}>{t("clinicalAlerts.modal.criticality.critical")}</option>
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 relative">
                {paginatedAlerts.length > 0 ? (
                    paginatedAlerts.map((alert) => (
                        <div
                            key={alert.id}
                            className="group relative flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 sm:p-6 bg-white/40 dark:bg-white/[0.02] border border-gray-100 dark:border-white/5 rounded-3xl shadow-theme-xs hover:shadow-theme-md transition-all duration-300 hover:border-brand-400/50 overflow-hidden"
                        >
                            <div className="flex flex-row items-center gap-4 sm:gap-6 w-full min-w-0">
                                {/* Icon / Visual Indicator */}
                                <div className={`flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-2xl shrink-0 shadow-sm transition-transform group-hover:scale-110 ${alert.criticality === Criticality.CRITICAL
                                    ? "bg-red-50 text-red-600 dark:bg-red-500/10"
                                    : alert.criticality === Criticality.MEDIUM
                                        ? "bg-orange-50 text-orange-600 dark:bg-orange-500/10"
                                        : "bg-accent-tint text-accent-text"
                                    }`}>
                                    <AlertTriangle className="w-6 h-6 sm:w-7 sm:h-7" />
                                </div>
 
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                                        <h3 className="text-base sm:text-lg font-bold text-content-primary group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors truncate">
                                            {alert.name}
                                        </h3>
                                        {getCriticalityBadge(alert.criticality)}
                                    </div>
                                    <p className="text-xs sm:text-sm text-content-tertiary line-clamp-2 leading-relaxed" title={alert.context}>
                                        {alert.context}
                                    </p>
                                </div>
                            </div>
 
                            {/* Actions bar inside the card */}
                            <div className="flex items-center gap-2 mt-4 sm:mt-0 sm:ml-6 ml-auto sm:opacity-0 sm:group-hover:opacity-100 transition-all transform sm:translate-x-2 sm:group-hover:translate-x-0">
                                <button
                                    onClick={() => onEdit(alert)}
                                    className="inline-flex items-center justify-center p-2.5 sm:p-3 rounded-xl bg-gray-100/50 sm:bg-gray-50 text-gray-500 hover:bg-brand-50 hover:text-brand-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-brand-500/10 dark:hover:text-brand-400 transition-all shadow-sm"
                                    title={t("common.edit")}
                                >
                                    <Edit2 className="w-4 h-4 sm:w-5 sm:h-5" />
                                </button>
                                <button
                                    onClick={() => handleDelete(alert.id)}
                                    className="inline-flex items-center justify-center p-2.5 sm:p-3 rounded-xl bg-gray-100/50 sm:bg-gray-50 text-gray-500 hover:bg-red-50 hover:text-red-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-red-500/10 dark:hover:text-red-400 transition-all shadow-sm"
                                    title={t("common.delete")}
                                >
                                    <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="p-10 text-center bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
                        <p className="text-content-tertiary">{t("clinicalAlerts.list.empty")}</p>
                    </div>
                )}
            </div>

            <Pagination
                offset={offset}
                total={filteredAlerts.length}
                pageSize={pageSize}
                onNext={handleNext}
                onPrev={handlePrev}
            />
        </div>
    );
};

export default ClinicalAlertList;
